/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import colors from '@sportninja/common/constants/appColors'
import useWindowSize from 'src/hooks/useWindowSize'
import { useModalScroll } from 'src/hooks/useModalScroll'
import { useSidebarState } from 'src/hooks/useSidebarState'
import { fullScreenOverlayBounds } from './utils/fullScreenOverlayBounds'
import {
  FULL_SCREEN_PAGE_GUTTER,
  FULL_SCREEN_PAGE_MAX_WIDTH,
} from './constants'

const TRANSITION_DURATION = 300

interface FullScreenModalProps {
  isOpen: boolean
  /**
   * - `form` (default): the 1280px column inset 88px each side — the Edit
   *   Player form's width.
   * - `page`: a wide column (1600px max, 24px gutter) for the Edit Player
   *   and Player Details modals.
   */
  width?: 'form' | 'page'
  /**
   * The z-index of the overlay this modal is opened FROM (e.g. the admin
   * Registration Details modal). Set it and the modal portals to the body,
   * stacks one layer above the host and covers the whole viewport. Omit it on
   * a page.
   */
  hostZIndex?: number
  children: React.ReactNode
}

/**
 * A full-screen form shell: covers the page below the top menu and beside the
 * app sidebar, fades + slides in, and locks the page scroll while open.
 * Children mount when it opens and unmount after the close transition, so
 * their state (a Formik form, a fetch) starts fresh every time.
 *
 * Content only — compose the title row with `FullScreenModalHeader`.
 */
const FullScreenModal: React.FC<FullScreenModalProps> = ({
  isOpen,
  width = 'form',
  hostZIndex,
  children,
}) => {
  const { width: windowWidth } = useWindowSize()
  const isMobile = windowWidth <= 768
  const [sidebarCollapsed] = useSidebarState()
  const sidebarWidth = isMobile ? 0 : sidebarCollapsed ? 64 : 284
  const bounds = fullScreenOverlayBounds({ hostZIndex, isMobile, sidebarWidth })

  const { openModal, closeModal } = useModalScroll()
  const [isMounted, setIsMounted] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (isOpen) {
      openModal()
      setIsMounted(true)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsVisible(true))
      })
    } else {
      setIsVisible(false)
      const timer = setTimeout(() => {
        setIsMounted(false)
        closeModal()
      }, TRANSITION_DURATION)
      return () => clearTimeout(timer)
    }
  }, [isOpen, openModal, closeModal])

  if (!isMounted) {
    return null
  }

  const overlay = (
    <div
      css={css`
        ${overlayStyle}
        z-index: ${bounds.zIndex};
        top: ${bounds.top}px;
        left: ${bounds.left}px;
        opacity: ${isVisible ? 1 : 0};
        transform: translateY(${isVisible ? '0' : '20px'});
      `}
    >
      <div css={[contentWrapperStyle, width === 'page' && pageWidthStyle]}>
        {children}
      </div>
    </div>
  )

  // Over a host, render outside its DOM so no transformed or clipping
  // ancestor inside the host modal can trap the fixed overlay.
  return hostZIndex === undefined
    ? overlay
    : ReactDOM.createPortal(overlay, document.body)
}

export default FullScreenModal

// ---- styled ----
const overlayStyle = css`
  position: fixed;
  right: 0;
  bottom: 0;
  background-color: ${colors.SECONDARY_400};
  display: flex;
  flex-direction: column;
  transition: opacity ${TRANSITION_DURATION}ms ease-out,
    transform ${TRANSITION_DURATION}ms ease-out, left 0.2s ease-in-out;
`

const contentWrapperStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 24px 88px 0px;

  @media (max-width: 768px) {
    max-width: none;
    padding: 0;
  }
`

// The wide page column (see ./constants). The gutter keeps the content off
// the edges when the space is narrower. Composed after contentWrapperStyle, so
// it also overrides its max-width and its phone padding.
const pageWidthStyle = css`
  max-width: ${FULL_SCREEN_PAGE_MAX_WIDTH}px;
  padding-left: ${FULL_SCREEN_PAGE_GUTTER}px;
  padding-right: ${FULL_SCREEN_PAGE_GUTTER}px;
`
